import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';
import GuideCTA from '../components/GuideCTA';

const GuideLettreMotivation = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>Optimiser sa lettre de motivation: structure AIDA et personnalisation</title>
                <meta name="description" content="Guide complet pour rédiger une lettre de motivation percutante: structure AIDA, personnalisation par entreprise, exemples concrets et techniques de rédaction." />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/lettre-motivation" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {"@type": "ListItem","position": 1,"name": "Accueil","item": "https://www.cv-analyse.fr/"},
                    {"@type": "ListItem","position": 2,"name": "Lettre de motivation","item": "https://www.cv-analyse.fr/guide/lettre-motivation"}
                  ]
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "headline": "Optimiser sa lettre de motivation",
                  "description": "Guide complet pour rédiger une lettre de motivation percutante",
                  "author": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "publisher": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "dateModified": "2026-08-15",
                  "mainEntityOfPage": {"@type": "WebPage", "@id": "https://www.cv-analyse.fr/guide/lettre-motivation"}
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment rédiger une lettre de motivation percutante",
                  "description": "Maîtrisez la méthode AIDA pour rédiger une lettre de motivation qui accroche les recruteurs et déclenche un entretien.",
                  "step": [
                    {"@type": "HowToStep", "name": "Analyser l'offre et l'entreprise", "text": "Identifiez les besoins de l'employeur et les valeurs de l'entreprise avant d'écrire."},
                    {"@type": "HowToStep", "name": "Rédiger une accroche percutante (Attention)", "text": "Commencez par une phrase forte qui capte immédiatement l'attention du recruteur."},
                    {"@type": "HowToStep", "name": "Démontrer sa valeur ajoutée (Intérêt)", "text": "Montrez en quoi votre profil répond précisément aux besoins du poste avec des exemples concrets."},
                    {"@type": "HowToStep", "name": "Exprimer sa motivation (Désir)", "text": "Expliquez pourquoi ce poste et cette entreprise en particulier vous motivent."},
                    {"@type": "HowToStep", "name": "Conclure avec un appel à l'action (Action)", "text": "Proposez un entretien et remerciez le recruteur pour sa lecture."}
                  ]
                }
                `}</script>
            </Helmet>

            {/* Fil d'Ariane */}
            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600" itemScope itemType="https://schema.org/BreadcrumbList">
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <Link to="/" itemProp="item" className="hover:text-slate-900">
                                <span itemProp="name">Accueil</span>
                            </Link>
                            <meta itemProp="position" content="1" />
                        </li>
                        <li className="text-slate-400">/</li>
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <span itemProp="name" className="text-slate-900">Lettre de motivation</span>
                            <meta itemProp="position" content="2" />
                        </li>
                    </ol>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">✉️</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            Optimiser sa lettre de motivation
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Rédigez une lettre de motivation qui marque les esprits avec la méthode AIDA
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="lettre-motivation" />

            {/* Table des matières */}
            <div className="card-surface mx-4 mt-4 max-w-4xl mx-auto">
                <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Table des matières</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <a href="#aida" className="flex items-center p-3 rounded-xl hover:bg-teal-50 transition-colors">
                            <span className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-3 text-teal-600 font-bold">1</span>
                            <span className="text-gray-700">Structure AIDA</span>
                        </a>
                        <a href="#personnalisation" className="flex items-center p-3 rounded-xl hover:bg-teal-50 transition-colors">
                            <span className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-3 text-teal-600 font-bold">2</span>
                            <span className="text-gray-700">Personnalisation par entreprise</span>
                        </a>
                        <a href="#formulations" className="flex items-center p-3 rounded-xl hover:bg-teal-50 transition-colors">
                            <span className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-3 text-teal-600 font-bold">3</span>
                            <span className="text-gray-700">Formulations percutantes</span>
                        </a>
                        <a href="#exemples" className="flex items-center p-3 rounded-xl hover:bg-teal-50 transition-colors">
                            <span className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-3 text-teal-600 font-bold">4</span>
                            <span className="text-gray-700">Exemples concrets</span>
                        </a>
                    </div>
                </div>
            </div>

            <article className="px-6 py-16 mx-auto max-w-5xl">

                {/* Section 1: Structure AIDA */}
                <div id="aida" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">📝</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Structure AIDA</h2>
                        </div>
                        
                        <div className="bg-gradient-to-r from-teal-50 to-cyan-100 rounded-2xl p-6 mb-6">
                            <h3 className="text-xl font-bold text-teal-900 mb-3">💡 La méthode AIDA</h3>
                            <p className="text-gray-700">AIDA = Attention, Intérêt, Désir, Action. Cette structure éprouvée maximise l'impact de votre lettre.</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-red-900 mb-3">🎯 A - Attention</h3>
                                    <p className="text-gray-700 text-sm mb-4">Accrochez dès la première phrase :</p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Chiffre marquant</li>
                                        <li>• Question pertinente</li>
                                        <li>• Défi spécifique</li>
                                        <li>• Référence à l'entreprise</li>
                                    </ul>
                                </div>
                                
                                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-orange-900 mb-3">🔍 I - Intérêt</h3>
                                    <p className="text-gray-700 text-sm mb-4">Développez votre valeur :</p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Compétences clés</li>
                                        <li>• Expériences pertinentes</li>
                                        <li>• Réalisations marquantes</li>
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-green-900 mb-3">💚 D - Désir</h3>
                                    <p className="text-gray-700 text-sm mb-4">Créez l'envie de vous rencontrer :</p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Motivation pour le poste</li>
                                        <li>• Alignement avec l'entreprise</li>
                                        <li>• Vision partagée</li>
                                    </ul>
                                </div>
                                
                                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-blue-900 mb-3">⚡ A - Action</h3>
                                    <p className="text-gray-700 text-sm mb-4">Concluez par un appel clair :</p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Demande d'entretien</li>
                                        <li>• Disponibilité</li>
                                        <li>• Contact direct</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Personnalisation par entreprise */}
                <div id="personnalisation" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">🎯</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Personnalisation par entreprise</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-cyan-900 mb-3">🔍 Recherche approfondie</h3>
                                    <p className="text-gray-700 text-sm mb-4">Informez-vous sur l'entreprise :</p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Mission et valeurs</li>
                                        <li>• Actualités récentes</li>
                                        <li>• Défis actuels</li>
                                        <li>• Culture d'entreprise</li>
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-purple-900 mb-3">💡 Adaptation du message</h3>
                                <p className="text-gray-700 text-sm mb-4">Personnalisez votre approche :</p>
                                <div className="space-y-3">
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-purple-500">
                                        <p className="text-purple-700 text-sm"><strong>Startup :</strong> Innovation, agilité, croissance</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-blue-500">
                                        <p className="text-blue-700 text-sm"><strong>Grand groupe :</strong> Stabilité, processus, expertise</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-green-500">
                                        <p className="text-green-700 text-sm"><strong>ONG :</strong> Mission, impact, engagement</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Formulations percutantes */}
                <div id="formulations" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">⚡</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Formulations percutantes</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-blue-900 mb-3">🎯 Accroches efficaces</h3>
                                    <p className="text-gray-700 text-sm mb-4">Exemples d'ouvertures percutantes :</p>
                                    <div className="space-y-3">
                                        <div className="bg-white p-3 rounded-lg border-l-4 border-blue-500">
                                            <p className="text-blue-700 text-sm">"Avec 5 ans d'expérience en développement web, j'ai contribué à l'augmentation du CA de 40% chez [Entreprise précédente]."</p>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg border-l-4 border-green-500">
                                            <p className="text-green-700 text-sm">"Votre innovation en IA m'inspire particulièrement, et je souhaite apporter mon expertise en machine learning à votre équipe."</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-green-900 mb-3">💪 Phrases de motivation</h3>
                                <p className="text-gray-700 text-sm mb-4">Formulations qui marquent :</p>
                                <ul className="text-sm text-gray-600 space-y-2">
                                    <li>• "Je suis convaincu que..."</li>
                                    <li>• "Mon expérience en... me permet de..."</li>
                                    <li>• "Je souhaite contribuer à..."</li>
                                    <li>• "Votre mission résonne avec mes valeurs..."</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 4: Exemples concrets */}
                <div id="exemples" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">💡</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Exemples concrets</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-purple-900 mb-3">👨‍💻 Développeur → Tech Lead</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Accroche :</strong>
                                        <p className="text-gray-600">"Votre vision de l'innovation technologique m'inspire, et je souhaite apporter mon expertise en architecture microservices à votre équipe."</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Intérêt :</strong>
                                        <p className="text-gray-600">"Avec 6 ans d'expérience en développement, j'ai mené 3 projets d'envergure et formé 5 développeurs junior."</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Action :</strong>
                                        <p className="text-gray-600">"Je serais ravi d'échanger sur ma vision du leadership technique lors d'un entretien."</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-teal-900 mb-3">👩‍💼 Commercial → Marketing</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Accroche :</strong>
                                        <p className="text-gray-600">"Votre stratégie de croissance digitale m'enthousiasme, et je souhaite contribuer à votre expansion avec mon expertise commerciale."</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Intérêt :</strong>
                                        <p className="text-gray-600">"Mes 4 ans de vente B2B m'ont appris à comprendre les besoins clients et à créer des campagnes ciblées."</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Action :</strong>
                                        <p className="text-gray-600">"Je serais honoré de partager mes idées pour développer votre marché lors d'un échange."</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <GuideCTA
                    title="Générez votre lettre de motivation avec l'IA"
                    description="Basée sur votre CV et l'offre d'emploi ciblée, personnalisée en 30 secondes — score et 3 conseils offerts."
                    ctaText="Générer ma lettre de motivation"
                    secondaryText="Voir les tarifs"
                    theme="teal"
                />

                {/* Navigation entre guides */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Link to="/guide/cv-reconversion-professionnelle" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-orange-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-orange-700">Guide précédent</h3>
                            <p className="text-sm text-gray-600">CV pour reconversion</p>
                        </div>
                    </Link>
                    
                    <Link to="/guides" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-teal-700">Retour aux guides</h3>
                            <p className="text-sm text-gray-600">Voir tous nos guides CV</p>
                        </div>
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center ml-4 group-hover:bg-teal-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </div>
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideLettreMotivation;
